import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useMemo, useState } from "react";
import { isSameDay, startOfLocalDay } from "@/lib/date";

interface CalendarPickerProps {
  selectedDate: Date;
  onSelect: (date: Date) => void;
  onClose: () => void;
}

const monthFormatter = new Intl.DateTimeFormat("en-US", { month: "long", year: "numeric" });

export function CalendarPicker({ selectedDate, onSelect, onClose }: CalendarPickerProps) {
  const [viewDate, setViewDate] = useState(
    new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
  );
  const today = startOfLocalDay(new Date());

  const monthMeta = useMemo(() => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { daysInMonth, startingDayOfWeek: firstDay.getDay() };
  }, [viewDate]);

  const handleDateSelect = (day: number) => {
    const value = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    onSelect(value);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/55 sm:items-center" onClick={onClose}>
      <div
        className="mx-4 w-full max-w-md rounded-t-2xl bg-[var(--color-bg-elevated)] p-6 sm:mb-4 sm:rounded-2xl"
        onClick={event => event.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <button
            onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-text-secondary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
            aria-label="Previous month"
          >
            <ChevronLeft size={20} />
          </button>

          <h2 className="text-lg text-[var(--color-text-primary)]">{monthFormatter.format(viewDate)}</h2>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-text-secondary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
              aria-label="Next month"
            >
              <ChevronRight size={20} />
            </button>
            <button
              onClick={onClose}
              className="flex h-10 w-10 items-center justify-center rounded-lg text-[var(--color-text-secondary)] transition-colors duration-[var(--transition-base)] hover:bg-[var(--color-bg-hover)]"
              aria-label="Close calendar"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-2">
          {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map(day => (
            <div key={day} className="py-2 text-center text-sm text-[var(--color-text-tertiary)]">
              {day}
            </div>
          ))}

          {Array.from({ length: monthMeta.startingDayOfWeek }).map((_, index) => (
            <div key={`empty-${index}`} />
          ))}

          {Array.from({ length: monthMeta.daysInMonth }).map((_, index) => {
            const day = index + 1;
            const value = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
            const isSelected = isSameDay(value, selectedDate);
            const isCurrentDay = isSameDay(value, today);
            const isFuture = value > today;

            return (
              <button
                key={day}
                onClick={() => handleDateSelect(day)}
                disabled={isFuture}
                className={`h-11 rounded-lg text-base transition-all duration-[var(--transition-base)] ${
                  isSelected
                    ? "bg-[var(--color-accent-muted)] text-[var(--color-text-primary)]"
                    : isCurrentDay
                      ? "border border-[var(--color-border-medium)] bg-[var(--color-bg-input)] text-[var(--color-text-primary)]"
                      : "text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)]"
                } ${isFuture ? "cursor-not-allowed opacity-30" : ""}`}
              >
                {day}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
